import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../../components/ui/button";
import { Download, FileX, FileText, Mail, X, Loader2, Files, Image, File, Users, Grid3X3, LayoutGrid, Maximize2, Eye, Quote, Package, ShoppingCart, CheckCircle, Clock, AlertCircle, RefreshCw, Save, Plus } from "lucide-react";
import { useToast } from "../../../../components/ui/use-toast";
import { Badge } from "../../../../components/ui/badge";
import "../../components/CommentsSection";
import { saveQuotationData } from "../../Utils/utils";
import { createPurchaseOrder } from "../../../../../ApiCalls/mantisFlowsManangment";
// Import new components
import { SupplierCard } from "./components/SupplierCard";
import { ComparativeTable } from "./components/ComparativeTable";
import { KanbanView } from "./components/KanbanView";
import QuotationModal from "./components/QuotationModal";
import PODetailsForm from "./components/PODetailsForm";

// Import API functions for Purchase Orders
import { getQuotationItemsByOrderId, assignItemsToSuppliers, removeItemSupplierAssignment } from "../../../../../ApiCalls/mantisFlowsManangment";

// Purchase Order Card Component
const PurchaseOrderCard = ({ po, supplier, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300";
      case "confirmed":
        return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300";
      case "received":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300";
      case "cancelled":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return <Mail className="h-4 w-4" />;
      case "confirmed":
        return <CheckCircle className="h-4 w-4" />;
      case "received":
        return <Package className="h-4 w-4" />;
      case "cancelled":
        return <X className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded flex items-center justify-center">
            <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-slate-100">
              PO #{po.id}
            </h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {supplier?.name || "Unknown Supplier"}
            </p>
          </div>
        </div>
        <Badge
          className={`${getStatusColor(po.status)} flex items-center space-x-1`}
        >
          {getStatusIcon(po.status)}
          <span>{po.status || "pending"}</span>
        </Badge>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Items:</span>
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {po.itemsCount || 0}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Total:</span>
          <span className="font-medium text-slate-900 dark:text-slate-100">
            ${po.totalAmount || "0.00"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Created:</span>
          <span className="text-slate-600 dark:text-slate-400">
            {po.createdAt?.slice(0, 10) || "N/A"}
          </span>
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onViewDetails(po)}
        className="w-full text-sm"
      >
        <Eye className="h-4 w-4 mr-2" />
        View Details
      </Button>
    </div>
  );
};

// PO Modal Component - moved outside renderMainContent to prevent recreation
const POModal = ({
  open,
  onClose,
  content,
  onCreate,
  showPoDetailsForm,
  setShowPoDetailsForm,
  creatingPO,
  handleFormBack,
  mantisBusiness,
}) => {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {showPoDetailsForm
                ? "Detalles de la Orden de Compra"
                : content.hasDiscrepancies
                ? "Inconsistencias encontradas"
                : "Listo para crear Orden de Compra"}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={creatingPO}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {showPoDetailsForm ? (
            // PO Details Form
            <PODetailsForm
              initialPoDetails={{}}
              onCreate={onCreate}
              creatingPO={creatingPO}
              onBack={handleFormBack}
              mantisBusiness={mantisBusiness}
            />
          ) : (
            // Original content (discrepancies/summary)
            <>
              {content.hasDiscrepancies ? (
                <>
                  <p className="mb-4 text-slate-700 dark:text-slate-300">
                    Algunos articulos tienen una cantidad asignada que no
                    coincide con la cantidad requerida para ordenar. Por favor,
                    revise:
                  </p>
                  <ul className="mb-4 max-h-40 overflow-y-auto text-sm space-y-3">
                    {content.discrepancies.map((d, idx) => (
                      <li
                        key={d.requestId}
                        className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-700"
                      >
                        <span className="font-medium text-red-900 dark:text-red-100">
                          {d.items[0]?.item_description ||
                            `Request ID: ${d.requestId}`}
                        </span>
                        <div className="mt-2 space-y-1">
                          <div>
                            Cantidad asignada:{" "}
                            <span className="font-bold text-blue-700">
                              {parseFloat(d.sumQuantity).toFixed(2)}{" "}
                              {d.items[0]?.unit || ""}
                            </span>
                          </div>
                          <div>
                            Cantidad requerida para ordenar:{" "}
                            <span className="font-bold text-amber-700">
                              {parseFloat(d.quantityToOrder).toFixed(2)}{" "}
                              {d.items[0]?.unit || ""}
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="mb-4 text-slate-700 dark:text-slate-300">
                    Todas las cantidades asignadas coinciden con las cantidades
                    requeridas para ordenar. Resumen:
                  </p>
                  <ul className="mb-4 max-h-40 overflow-y-auto text-sm space-y-3">
                    {content.summary.map((s, idx) => (
                      <li
                        key={s.requestId}
                        className="p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-700"
                      >
                        <span className="font-medium text-green-900 dark:text-green-100">
                          {s.items[0]?.item_description ||
                            `Request ID: ${s.requestId}`}
                        </span>
                        <div className="mt-2 space-y-1">
                          <div>
                            Cantidad asignada:{" "}
                            <span className="font-bold text-blue-700">
                              {parseFloat(s.sumQuantity).toFixed(2)}{" "}
                              {s.items[0]?.unit || ""}
                            </span>
                          </div>
                          <div>
                            Cantidad requerida para ordenar:{" "}
                            <span className="font-bold text-green-700">
                              {parseFloat(s.quantityToOrder).toFixed(2)}{" "}
                              {s.items[0]?.unit || ""}
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <Button
            variant="outline"
            onClick={
              showPoDetailsForm ? () => setShowPoDetailsForm(false) : onClose
            }
            disabled={creatingPO}
          >
            {showPoDetailsForm ? "Volver" : "Cancelar"}
          </Button>
          {!showPoDetailsForm && (
            <Button
              variant="default"
              onClick={() => setShowPoDetailsForm(true)}
              disabled={creatingPO}
              className="text-white"
            >
              Continuar
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const QuotationsSection = ({
  data,
  addLog,
  selectedSupplierId,
  onSupplierSelect,
  suppliers = [],
  selectedSuppliersIds = [],
  allQuotationItems = [],
  isLoadingAllQuotationItems = false,
  quotationItems = [],
  orderItems = [],
  // New props for PO management
  currentUser,
  clientId,
  mantisBusiness,
  // Add orderSuppliers prop for suppliers that have already responded
  orderSuppliers = [],
}) => {
  // Estados locales para UI
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [supplierDocuments, setSupplierDocuments] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // 'grid', 'kanban', 'pos'
  const { toast } = useToast();

  // Estados para Purchase Orders
  const [purchaseOrders] = useState([]);
  const [isLoadingPOs, setIsLoadingPOs] = useState(false);
  const [assignmentStats] = useState(null);
  const [assignedItems, setAssignedItems] = useState([]);
  const [isLoadingQuotationItems, setIsLoadingQuotationItems] = useState(false);

  // Estados para la asignación de proveedores
  const [itemSupplierAssignments, setItemSupplierAssignments] = useState({});
  const [isAssigning, setIsAssigning] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Add state for PO modal
  const [showPOModal, setShowPOModal] = useState(false);
  const [poModalContent, setPOModalContent] = useState({
    discrepancies: [],
    summary: null,
    hasDiscrepancies: false,
  });
  const [creatingPO, setCreatingPO] = useState(false);

  const [showPoDetailsForm, setShowPoDetailsForm] = useState(false);

  // Add state for quotation creation modal
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  // Simple callback to prevent PODetailsForm re-renders
  const handleFormBack = useCallback(() => {
    setShowPoDetailsForm(false);
  }, []);

  // Create purchase order function at component level
  const handleCreatePurchaseOrder = useCallback(
    async (formData = {}) => {
      setCreatingPO(true);
      try {
        // Get current data from state
        const respondedContractors = Array.isArray(orderSuppliers)
          ? orderSuppliers
          : [];
        const safeQuotationItems = Array.isArray(assignedItems)
          ? assignedItems
          : [];
        const receivedQuotationItems = safeQuotationItems;

        for (let i = 0; i < respondedContractors.length; i++) {
          let supplier = respondedContractors[i];
          let supplierItems = receivedQuotationItems.filter(
            (item) => item.supplier_id === supplier.id
          );
          // Find the maximum delivery_time_days
          const maxDeliveryTimeDays = Math.max(
            ...supplierItems.map((item) => Number(item.delivery_time_days) || 0)
          );

          // Calculate the expected delivery date
          const expectedDate = new Date();
          expectedDate.setDate(expectedDate.getDate() + maxDeliveryTimeDays);
          const expectedDeliveryDate = expectedDate.toISOString().slice(0, 10);

          let poData = {
            currency: supplierItems[0]?.currency || "COP",
            expectedDeliveryDate,
            payment_terms: formData.payment_terms || "",
            shipping_terms: formData.shipping_terms || "",
            notes: formData.notes || "",
            address: formData.address || "",
            city: formData.city || "",
            zip_code: formData.zip_code || "",
            shipping_address: formData.shipping_address || "",
            tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null,
            shipping_cost: formData.shipping_cost
              ? parseFloat(formData.shipping_cost)
              : null,
            other_costs: formData.other_costs
              ? parseFloat(formData.other_costs)
              : null,
            discount_percentage: formData.discount_percentage
              ? parseFloat(formData.discount_percentage)
              : null,
          };

          const res = await createPurchaseOrder(
            clientId,
            data.id,
            poData,
            supplier.id,
            supplierItems,
            currentUser?.id
          );
          if (res.success) {
          }
        }
        setShowPOModal(false);
        setShowPoDetailsForm(false);
        toast.success("Orden de compra creada exitosamente!");
        // Optionally refresh data
        await loadAllData();
      } catch (e) {
        console.error("Error creating purchase order:", e);
        toast({
          title: "Error",
          description: "Failed to create purchase order",
          variant: "destructive",
        });
      } finally {
        setCreatingPO(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderSuppliers,
      assignedItems,
      clientId,
      data?.id,
      currentUser?.id,
      mantisBusiness.name,
      toast,
    ]
  );

  // Load existing assignments when component mounts or data changes
  useEffect(() => {
    if (clientId && data?.id) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, data?.id]);

  // Consolidated function to load all data in one call
  const loadAllData = async () => {
    if (!clientId || !data?.id) return;

    setIsLoadingQuotationItems(true);
    try {
      const [quotationItemsResponse] = await Promise.all([
        getQuotationItemsByOrderId(clientId, data.id),
      ]);

      // Process quotation items data
      const quotationItems = quotationItemsResponse.data || [];
      // Extract supplier assignments from quotation items if they have supplier info
      const assignments = {};
      if (Array.isArray(quotationItems)) {
        quotationItems.forEach((item) => {
          if (item.supplier_id) {
            assignments[item.id] = item.supplier_id;
          }
        });
      }

      setItemSupplierAssignments(assignments);
      setAssignedItems(quotationItems);
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error",
        description: "Failed to load quotation data",
        variant: "destructive",
      });
    } finally {
      setIsLoadingQuotationItems(false);
    }
  };

  // Handle supplier assignment change
  
  // Save all supplier assignments
  const saveSupplierAssignments = async () => {
    if (!hasUnsavedChanges) return;

    setIsAssigning(true);
    try {
      const assignments = Object.entries(itemSupplierAssignments).map(
        ([itemId, supplierId]) => ({
          itemId: parseInt(itemId),
          supplierId: parseInt(supplierId),
        })
      );

      await assignItemsToSuppliers(
        clientId,
        data.id,
        assignments,
        currentUser?.id
      );

      setHasUnsavedChanges(false);
      await loadAllData();

      addLog?.(
        `Supplier assignments saved successfully for ${assignments.length} items`,
        "success"
      );
    } catch (error) {
      console.error("Error saving assignments:", error);
      addLog?.(`Error saving supplier assignments: ${error.message}`, "error");
    } finally {
      setIsAssigning(false);
    }
  };

  // Remove assignment for a specific item
  
  // Create Purchase Orders by supplier
  const createPOsBySupplier = async () => {
    if (Object.keys(itemSupplierAssignments).length === 0) {
      addLog?.(
        "No supplier assignments found. Please assign suppliers to items first.",
        "warning"
      );
      return;
    }

    try {
      // Get current data from state
      const respondedContractors = Array.isArray(orderSuppliers)
        ? orderSuppliers
        : [];
      const safeQuotationItems = Array.isArray(assignedItems)
        ? assignedItems
        : [];
      const receivedQuotationItems = safeQuotationItems;

      for (let i = 0; i < respondedContractors.length; i++) {
        let supplier = respondedContractors[i];
        let supplierItems = receivedQuotationItems.filter(
          (item) => item.supplier_id === supplier.id
        );

        if (supplierItems.length === 0) continue; // Skip if no items for this supplier

        // Find the maximum delivery_time_days
        const maxDeliveryTimeDays = Math.max(
          ...supplierItems.map((item) => Number(item.delivery_time_days) || 0)
        );

        // Calculate the expected delivery date
        const expectedDate = new Date();
        expectedDate.setDate(expectedDate.getDate() + maxDeliveryTimeDays);
        const expectedDeliveryDate = expectedDate.toISOString().slice(0, 10);

        let poData = {
          currency: supplierItems[0]?.currency || "COP",
          expectedDeliveryDate,
          payment_terms: "",
          shipping_terms: "",
          notes: "",
          address: "",
          city: "",
          zip_code: "",
          shipping_address: "",
          tax_rate: null,
          shipping_cost: null,
          other_costs: null,
          discount_percentage: null,
        };

        const res = await createPurchaseOrder(
          clientId,
          data.id,
          poData,
          supplier.id,
          supplierItems,
          currentUser?.id
        );
        if (res.success) {
          console.log(`PO created for supplier ${supplier.name}`);
        }
      }

      addLog?.(
        `Successfully created Purchase Orders for ${respondedContractors.length} suppliers`,
        "success"
      );
      await loadAllData();
    } catch (error) {
      console.error("Error creating POs:", error);
      addLog?.(`Error creating Purchase Orders: ${error.message}`, "error");
    }
  };

  // Open quotation creation modal
  const openQuotationModal = () => {
    setShowQuotationModal(true);
    // Scroll to top to ensure modal is visible
    window.scrollTo(0, 0);
  };

  // Handle quotation creation callback
  const handleQuotationCreated = async (quotationData) => {
    // Refresh data after quotation is created
    await loadAllData();

    // Add log entry
    addLog?.(
      `Quotation created successfully for supplier: ${quotationData.supplierName}`,
      "success"
    );
  };

  // Calculate assignment statistics
  const assignedItemsCount = Object.keys(itemSupplierAssignments).length;
  const totalItemsCount = assignedItems.length; // Use local assignedItems instead of allQuotationItems
  const unassignedItemsCount = totalItemsCount - assignedItemsCount;
  const assignedSuppliersCount = new Set(Object.values(itemSupplierAssignments))
    .size;

  // Effect to handle supplier selection persistence
  useEffect(() => {
    if (data?.id && selectedSupplierId) {
      localStorage.setItem(
        `selected_supplier_${data.id}`,
        JSON.stringify({ supplierId: selectedSupplierId })
      );
    }
  }, [selectedSupplierId, data?.id]);

  // Refresh function for manual reload
  const refreshData = async () => {
    setIsLoadingPOs(true);
    await loadAllData();
    setIsLoadingPOs(false);
  };

  const handleSelectSupplier = (supplierId) => {
    if (!supplierId) {
      if (data?.id) {
        localStorage.removeItem(`selected_supplier_${data.id}`);
      }
      if (onSupplierSelect) {
        onSupplierSelect(null);
      }
      return;
    }

    const selectedSupplier = suppliers.find(
      (supplier) => supplier.id === supplierId
    );

    if (selectedSupplier) {
      if (data?.id) {
        localStorage.setItem(
          `selected_supplier_${data.id}`,
          JSON.stringify({ supplierId })
        );
      }

      const supplierQuotationItems = assignedItems.filter(
        (item) => item.supplier_id === supplierId
      );
      if (supplierQuotationItems.length > 0) {
        saveQuotationData(
          supplierId,
          selectedSupplier,
          supplierQuotationItems,
          data,
          addLog
        );
      }

      if (onSupplierSelect) {
        onSupplierSelect(supplierId);
      }
    }
  };

  // Handle PO details viewing
  const handleViewPODetails = (po) => {
    // Here you could open a modal or navigate to a detailed view
    console.log("Viewing PO details:", po);
    toast({
      title: "PO Details",
      description: `Viewing details for Purchase Order #${po.id}`,
    });
  };

  // Handle quotation items updates
  const handleUpdateQuotationItems = (updatedItems) => {
    setAssignedItems(updatedItems);
    setHasUnsavedChanges(true);
  };

  const loadSupplierDocuments = async (supplierId) => {
    try {
      setIsLoadingDocuments(true);
      setShowDocumentPanel(true);
      // Simulated documents - replace with actual API call
      setSupplierDocuments([
        {
          id: 1,
          name: "Quotation Document",
          document_type: "pdf",
          url: "#",
          size: "2.5 MB",
          status: "Available",
        },
        {
          id: 2,
          name: "Technical Specifications",
          document_type: "pdf",
          url: "#",
          size: "1.8 MB",
          status: "Available",
        },
      ]);
    } catch (error) {
      console.error("Error loading supplier documents:", error);
      toast({
        title: "Error",
        description: "Failed to load supplier documents",
        variant: "destructive",
      });
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const renderDocumentPanel = () => (
    <div className="w-1/2 h-full overflow-auto border-r border-slate-200/50 dark:border-slate-700/50 bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-slate-900/50 dark:via-slate-800 dark:to-slate-900">
      <div className="p-6">
        {/* Header */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 shadow-sm mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  Supplier Documents
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  View and download documents provided by the supplier
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={() => {
                setShowDocumentPanel(false);
                setSelectedDocument(null);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Documents List */}
        {isLoadingDocuments ? (
          <div className="flex justify-center items-center py-12 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Loading documents...
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {supplierDocuments.length > 0 ? (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                  <Files className="h-5 w-5 mr-2 text-blue-500" />
                  Available Documents
                </h4>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {supplierDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className={`p-4 rounded cursor-pointer transition-all duration-200 flex items-center justify-between ${
                        selectedDocument?.id === doc.id
                          ? "bg-blue-50/80 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700 shadow-sm"
                          : "bg-slate-50/80 dark:bg-slate-800/60 hover:bg-slate-100/80 dark:hover:bg-slate-700/60 border border-slate-200/50 dark:border-slate-700/50"
                      }`}
                      onClick={() => setSelectedDocument(doc)}
                    >
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4 flex-shrink-0">
                          {doc.document_type === "pdf" ? (
                            <FileText className="h-6 w-6 text-white" />
                          ) : doc.document_type === "image" ? (
                            <Image className="h-6 w-6 text-white" />
                          ) : (
                            <File className="h-6 w-6 text-white" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm font-semibold block">
                            {doc.name}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {doc.document_type?.toUpperCase() || "Document"} •{" "}
                            {doc.size || "Unknown size"}
                          </span>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="text-xs flex-shrink-0"
                      >
                        {doc.status || "Available"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-12 shadow-sm text-center">
                <FileX className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  No Documents Available
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  This supplier hasn't uploaded any documents yet.
                </p>
              </div>
            )}

            {/* Document Viewer */}
            {selectedDocument && (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-slate-200/50 dark:border-slate-700/50 bg-gradient-to-r from-slate-50/50 to-transparent dark:from-slate-800/50">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {selectedDocument.name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="text-xs">
                        {selectedDocument.document_type}
                      </Badge>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          asChild
                        >
                          <a
                            href={selectedDocument.url}
                            download={selectedDocument.name}
                            title="Download document"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          asChild
                        >
                          <a
                            href={selectedDocument.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in new tab"
                          >
                            <Maximize2 className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-96 bg-slate-50/50 dark:bg-slate-900/50 p-4">
                  <div className="w-full h-full flex items-center justify-center bg-white dark:bg-slate-800 rounded border border-slate-200/50 dark:border-slate-700/50">
                    <div className="text-center">
                      <Eye className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Document preview
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderMainContent = () => {
    // Use orderSuppliers for suppliers that have already responded (for comparative table and displays)
    const respondedContractors = Array.isArray(orderSuppliers)
      ? orderSuppliers
      : [];
    const safeQuotationItems = Array.isArray(assignedItems)
      ? assignedItems
      : [];

    // Use all quotation items since they are already received if they exist
    const receivedQuotationItems = safeQuotationItems;
    console.log("ReceivedQuotationItems:", receivedQuotationItems);

    // Helper: Group items by initial_purchase_request_item_id
    const groupByInitialRequestId = (items) => {
      const groups = {};
      items.forEach((item) => {
        const key = item.initial_purchase_request_item_id;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
      return groups;
    };

    // Helper: Check for quantity discrepancies
    const checkQuantityDiscrepancies = (items) => {
      const groups = groupByInitialRequestId(items);
      const discrepancies = [];
      const summary = [];
      Object.entries(groups).forEach(([requestId, group]) => {
        const sumQuantity = group.reduce(
          (sum, item) => sum + (parseFloat(item.quantity) || 0),
          0
        );
        const quantityToOrder = parseFloat(group[0]?.quantity_to_order) || 0;
        summary.push({
          requestId,
          sumQuantity,
          quantityToOrder,
          items: group,
        });
        if (sumQuantity !== quantityToOrder) {
          discrepancies.push({
            requestId,
            sumQuantity,
            quantityToOrder,
            items: group,
          });
        }
      });
      return {
        discrepancies,
        summary,
        hasDiscrepancies: discrepancies.length > 0,
      };
    };

    // Helper: Check if all item groups have at least one item with quantity > 0
    const canShowCreatePOButton = () => {
      if (!receivedQuotationItems.length) return false;
      const groups = {};
      receivedQuotationItems.forEach((item) => {
        const key = item.initial_purchase_request_item_id;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
      // For each group, at least one item must have quantity > 0
      return Object.values(groups).every((group) =>
        group.some((item) => item.quantity > 0)
      );
    };

    // Placeholder for create purchase order
    const handleCreatePurchaseOrder = async (formData = {}) => {
      setCreatingPO(true);
      try {
        for (let i = 0; i < respondedContractors.length; i++) {
          let supplier = respondedContractors[i];
          let supplierItems = receivedQuotationItems.filter(
            (item) => item.supplier_id === supplier.id
          );

          // Update quantity field with quantity_to_order values
          supplierItems = supplierItems.map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity_to_order || item.quantity || 0),
          }));

          // Find the maximum delivery_time_days
          const maxDeliveryTimeDays = Math.max(
            ...supplierItems.map((item) => Number(item.delivery_time_days) || 0)
          );

          // Calculate the expected delivery date
          const expectedDate = new Date();
          expectedDate.setDate(expectedDate.getDate() + maxDeliveryTimeDays);
          const expectedDeliveryDate = expectedDate.toISOString().slice(0, 10);

          let poData = {
            currency: supplierItems[0]?.currency || "COP",
            expectedDeliveryDate,
            payment_terms: formData.payment_terms || "",
            shipping_terms: formData.shipping_terms || "",
            notes: formData.notes || "",
            address: formData.address || "",
            city: formData.city || "",
            zip_code: formData.zip_code || "",
            shipping_address: formData.shipping_address || "",
            tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null,
            shipping_cost: formData.shipping_cost
              ? parseFloat(formData.shipping_cost)
              : null,
            other_costs: formData.other_costs
              ? parseFloat(formData.other_costs)
              : null,
            discount_percentage: formData.discount_percentage
              ? parseFloat(formData.discount_percentage)
              : null,
          };

          const res = await createPurchaseOrder(
            clientId,
            data.id,
            poData,
            supplier.id,
            supplierItems,
            currentUser?.id
          );
          if (res.success) {
          }
        }
        setShowPOModal(false);
        setShowPoDetailsForm(false);
        toast.success("Orden de compra creada exitosamente!");
        // Optionally refresh data
        await loadAllData();
      } catch (e) {
        console.error("Error creating purchase order:", e);
        toast({
          title: "Error",
          description: "Failed to create purchase order",
          variant: "destructive",
        });
      } finally {
        setCreatingPO(false);
      }
    };

    return (
      <div
        className={`${
          showDocumentPanel ? "w-1/2" : "w-full"
        } h-full overflow-auto`}
      >
        <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-slate-900/50 dark:via-slate-800 dark:to-slate-900">
          {/* Header with View Toggle */}
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <Quote className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Cotizaciones
                  </h2>
                  <div className="flex items-center space-x-3 text-sm text-slate-500 dark:text-slate-400">
                    <span>{respondedContractors.length} proveedores</span>
                    <span>•</span>
                    <span>
                      {receivedQuotationItems.length} cotizaciones recibidas
                    </span>
                    <span>•</span>
                    <span>{safeQuotationItems.length} artículos totales</span>
                  </div>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2 bg-slate-100/80 dark:bg-slate-700/80 rounded p-1">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={`transition-all duration-200 ${
                    viewMode === "grid"
                      ? "bg-white dark:bg-slate-600 shadow-sm"
                      : ""
                  }`}
                >
                  <LayoutGrid className="h-4 w-4 mr-2" />
                  Grid
                </Button>
                <Button
                  variant={viewMode === "kanban" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                  className={`transition-all duration-200 ${
                    viewMode === "kanban"
                      ? "bg-white dark:bg-slate-600 shadow-sm"
                      : ""
                  }`}
                >
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Kanban
                </Button>
                <Button
                  variant={viewMode === "pos" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("pos")}
                  className={`transition-all duration-200 ${
                    viewMode === "pos"
                      ? "bg-white dark:bg-slate-600 shadow-sm"
                      : ""
                  }`}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Purchase Orders
                </Button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoadingQuotationItems || isLoadingAllQuotationItems ? (
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-16 shadow-sm text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
                Loading Quotation Data
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                We're gathering quotation details from all suppliers...
              </p>
            </div>
          ) : (
            <>
              {/* Assignment Statistics and Actions */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                          Supplier Assignments
                        </h3>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {assignedItemsCount} of {totalItemsCount} items
                          assigned to {assignedSuppliersCount} suppliers
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {assignedItemsCount}
                        </div>
                        <div className="text-xs text-green-700 dark:text-green-300">
                          Assigned
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                          {unassignedItemsCount}
                        </div>
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                          Pending
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                          {assignedSuppliersCount}
                        </div>
                        <div className="text-xs text-blue-700 dark:text-blue-300">
                          Suppliers
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {hasUnsavedChanges && (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700"
                      >
                        Unsaved Changes
                      </Badge>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={saveSupplierAssignments}
                      disabled={!hasUnsavedChanges || isAssigning}
                      className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      {isAssigning ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Assignments
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={createPOsBySupplier}
                      disabled={assignedItemsCount === 0}
                      className="border-purple-200 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Create POs
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openQuotationModal}
                      className="border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear Cotización
                    </Button>
                  </div>
                </div>
              </div>

              {/* Purchase Orders View */}
              {viewMode === "pos" ? (
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded flex items-center justify-center">
                        <ShoppingCart className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          Purchase Orders by Supplier
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Generated from supplier assignments
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-sm">
                      {purchaseOrders.length} POs created
                    </Badge>
                  </div>

                  {/* Assignment Summary */}
                  {assignmentStats && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded p-4 mb-6 border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-600 dark:text-green-400">
                              {assignmentStats.assignedItems || 0}
                            </div>
                            <div className="text-xs text-green-700 dark:text-green-300">
                              Items Assigned
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                              {assignmentStats.suppliersCount || 0}
                            </div>
                            <div className="text-xs text-blue-700 dark:text-blue-300">
                              Suppliers
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                              {purchaseOrders.length}
                            </div>
                            <div className="text-xs text-purple-700 dark:text-purple-300">
                              POs Generated
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={refreshData}
                          className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* PO Grid */}
                  {isLoadingPOs ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-green-500 mx-auto mb-3" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Loading Purchase Orders...
                        </span>
                      </div>
                    </div>
                  ) : purchaseOrders.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {purchaseOrders.map((po) => {
                        const supplier = suppliers.find(
                          (s) => s.id === po.supplierId
                        );
                        return (
                          <PurchaseOrderCard
                            key={po.id}
                            po={po}
                            supplier={supplier}
                            onViewDetails={handleViewPODetails}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center mx-auto mb-6">
                        <ShoppingCart className="h-10 w-10 text-slate-400 dark:text-slate-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
                        No Purchase Orders Yet
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                        Purchase Orders will appear here once you assign items
                        to suppliers and create POs from the Suppliers section.
                      </p>
                    </div>
                  )}
                </div>
              ) : /* Suppliers View */
              viewMode === "grid" ? (
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Supplier Response Status
                    </h3>
                    <Badge variant="outline" className="text-sm">
                      {respondedContractors.length} suppliers available
                    </Badge>
                  </div>

                  {respondedContractors.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                      {respondedContractors.map((supplier) => {
                        const hasQuotationItems = receivedQuotationItems.some(
                          (item) => item.supplier_id === supplier.id
                        );

                        return (
                          <SupplierCard
                            key={supplier.id}
                            supplier={supplier}
                            hasQuotationItems={hasQuotationItems}
                            isSelected={selectedSupplierId === supplier.id}
                            onSelect={handleSelectSupplier}
                            onViewDocuments={loadSupplierDocuments}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center mx-auto mb-6">
                        <Users className="h-10 w-10 text-slate-400 dark:text-slate-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
                        No Suppliers Available
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                        No suppliers were selected in the RFQ step or quotations
                        are still being created.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm overflow-hidden">
                  <KanbanView
                    suppliers={respondedContractors}
                    quotationItems={receivedQuotationItems}
                    selectedSupplierId={selectedSupplierId}
                    onSupplierSelect={handleSelectSupplier}
                    onViewDocuments={loadSupplierDocuments}
                  />
                </div>
              )}
              {console.log("assignedItems (quotation items)", assignedItems)}

              {/* Comparative Table */}
              {respondedContractors.length > 0 && (
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm overflow-hidden mb-6">
                  <ComparativeTable
                    suppliers={respondedContractors}
                    quotationItems={receivedQuotationItems}
                    selectedSupplierId={selectedSupplierId}
                    isLoading={isLoadingQuotationItems}
                    onUpdateQuotationItems={handleUpdateQuotationItems}
                  />
                  {console.log("DEBUG - ComparativeTable props:", {
                    suppliers: respondedContractors,
                    quotationItems: receivedQuotationItems,
                    selectedSupplierId,
                    isLoading: isLoadingQuotationItems,
                  })}
                </div>
              )}
              {/* Create Purchase Order Button */}
              {canShowCreatePOButton() && (
                <div className="flex justify-end mt-4">
                  <button
                    className="bg-blue-100 text-blue-700 rounded px-4 py-1 text-sm font-medium border border-blue-200 shadow-sm flex items-center"
                    onClick={() => {
                      // Group and check quantities
                      const check = checkQuantityDiscrepancies(
                        receivedQuotationItems
                      );
                      setPOModalContent(check);
                      setShowPOModal(true);
                    }}
                  >
                    Crear Orden de Compra
                  </button>
                </div>
              )}
              {/* PO Verification Modal */}
              <POModal
                open={showPOModal}
                onClose={() => {
                  if (!creatingPO) setShowPOModal(false);
                }}
                content={poModalContent}
                onCreate={handleCreatePurchaseOrder}
                showPoDetailsForm={showPoDetailsForm}
                setShowPoDetailsForm={setShowPoDetailsForm}
                creatingPO={creatingPO}
                handleFormBack={handleFormBack}
                mantisBusiness={mantisBusiness}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex overflow-hidden">
      {showDocumentPanel && renderDocumentPanel()}
      {renderMainContent()}
      <QuotationModal
        isOpen={showQuotationModal}
        onClose={() => setShowQuotationModal(false)}
        suppliers={suppliers}
        orderItems={orderItems}
        currentUser={currentUser}
        clientId={clientId}
        orderId={data?.id}
        onQuotationCreated={handleQuotationCreated}
      />
    </div>
  );
};


